const HOVER_QUERY = '(any-hover: hover)'
const TOUCH_QUERY = '(any-pointer: coarse)'
const COMPACT_QUERY = '(max-width: 768px)'
const WIDE_QUERY = '(min-width: 1200px)'

function finiteNumber(value, fallback = 0) {
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

function positiveNumber(value, fallback = 0) {
  const number = finiteNumber(value)
  return number > 0 ? number : fallback
}

function getWindow() {
  return typeof window === 'undefined' ? null : window
}

function matches(target, query) {
  return Boolean(target?.matchMedia?.(query)?.matches)
}

// iPadOS can request a desktop UA. Touch is only an identity signal in
// combination with the Mac platform; a Windows touch laptop remains a PC.
export function classifyDevice(navigatorLike) {
  const userAgent = String(navigatorLike?.userAgent || '')
  const platform = String(navigatorLike?.platform || '')
  if (/iPad/i.test(userAgent)) return 'ipad'
  if (/iPhone|iPod/i.test(userAgent)) return 'phone'
  if (/Android/i.test(userAgent)) return /Mobile/i.test(userAgent) ? 'phone' : 'unknown'
  const isMac = /MacIntel|Macintosh/i.test(platform) || /Macintosh/i.test(userAgent)
  if (isMac && finiteNumber(navigatorLike?.maxTouchPoints) > 1) return 'ipad'
  if (isMac || /Windows|Win32|Win64|X11|Linux|CrOS/i.test(`${platform} ${userAgent}`)) return 'pc'
  return 'unknown'
}

export function getLayoutTier(width) {
  const layoutWidth = Math.max(0, finiteNumber(width))
  if (layoutWidth <= 768) return 'compact'
  return layoutWidth < 1200 ? 'medium' : 'wide'
}

// Workspace panes must also fit browser emulation or desktop-UA phone windows.
// Use layout dimensions so opening the keyboard cannot switch a tablet's layout.
export function isPhoneWorkspace(display) {
  return Boolean(display?.isPhone || (!display?.isIPad && display?.width > 768 &&
    display.width <= 1024 && display.height > 0 && display.height <= 600))
}

function readLayoutTier(target, width) {
  const compact = target?.matchMedia?.(COMPACT_QUERY)
  const wide = target?.matchMedia?.(WIDE_QUERY)
  // CSS media queries retain fractional viewport precision and include a
  // desktop scrollbar; clientWidth is rounded and excludes that scrollbar.
  if (compact?.media && wide?.media) {
    if (compact.matches) return 'compact'
    return wide.matches ? 'wide' : 'medium'
  }
  return getLayoutTier(width)
}

export function readDisplayState(target = getWindow()) {
  const root = target?.document?.documentElement
  // Layout dimensions must not shrink to the keyboard's VisualViewport.
  const width = positiveNumber(root?.clientWidth, positiveNumber(target?.innerWidth))
  const height = positiveNumber(root?.clientHeight, positiveNumber(target?.innerHeight))
  const visualViewport = target?.visualViewport
  const left = Math.max(0, finiteNumber(visualViewport?.offsetLeft))
  const top = Math.max(0, finiteNumber(visualViewport?.offsetTop))
  const visibleWidth = positiveNumber(visualViewport?.width, width)
  const visibleHeight = positiveNumber(visualViewport?.height, height)
  const device = classifyDevice(target?.navigator)
  // Device orientation is stable while the software keyboard changes visibility.
  // Older Safari exposes window.orientation instead of screen.orientation.
  const screenOrientation = String(target?.screen?.orientation?.type || '')
  const orientation = /^(portrait|landscape)/.test(screenOrientation)
    ? screenOrientation.split('-')[0]
    : typeof target?.orientation === 'number'
      ? Math.abs(target.orientation) % 180 === 90 ? 'landscape' : 'portrait'
      : width > height ? 'landscape' : 'portrait'
  const layoutTier = readLayoutTier(target, width)
  const layoutMode = device === 'ipad'
    ? orientation === 'landscape' ? 'desktop' : 'mobile'
    : layoutTier === 'compact' ? 'mobile' : 'desktop'
  return {
    device,
    isIPad: device === 'ipad',
    isPhone: device === 'phone',
    layoutTier,
    orientation,
    layoutMode,
    width,
    height,
    hasTouch: finiteNumber(target?.navigator?.maxTouchPoints) > 0 ||
      Boolean(target && 'ontouchstart' in target) || matches(target, TOUCH_QUERY),
    hasHover: matches(target, HOVER_QUERY),
    viewport: {
      left,
      top,
      width: visibleWidth,
      height: visibleHeight,
      right: left + visibleWidth,
      bottom: top + visibleHeight
    }
  }
}

function sameState(previous, next) {
  if (!previous) return false
  return ['device', 'isIPad', 'isPhone', 'layoutTier', 'orientation', 'layoutMode', 'width', 'height', 'hasTouch', 'hasHover']
    .every(key => previous[key] === next[key]) &&
    ['left', 'top', 'width', 'height', 'right', 'bottom']
      .every(key => previous.viewport[key] === next.viewport[key])
}

function syncRootAttributes(target, state) {
  const root = target?.document?.documentElement
  if (!root?.setAttribute) return
  for (const [name, value] of [['data-device', state.device], ['data-layout-tier', state.layoutTier], ['data-layout-mode', state.layoutMode]]) {
    if (root.getAttribute?.(name) !== value) root.setAttribute(name, value)
  }
  for (const name of ['height', 'width', 'top', 'left']) {
    root.style?.setProperty(`--app-visible-${name}`, `${state.viewport[name]}px`)
  }
}

// One observer owns all native listeners. Consumers use the ref-counted Vue
// adapter rather than creating a listener for every page or popover.
export function observeDisplayState(listener, target = getWindow()) {
  if (typeof listener !== 'function') throw new TypeError('A display-state listener is required')
  let previous
  let stopped = false
  const cleanup = []
  const update = () => {
    if (stopped) return
    const next = readDisplayState(target)
    syncRootAttributes(target, next)
    if (sameState(previous, next)) return
    previous = next
    listener(next)
  }
  const listen = (owner, event) => {
    if (!owner?.addEventListener) return
    owner.addEventListener(event, update)
    cleanup.push(() => owner.removeEventListener(event, update))
  }
  listen(target, 'resize')
  listen(target, 'orientationchange')
  listen(target?.screen?.orientation, 'change')
  listen(target?.visualViewport, 'resize')
  listen(target?.visualViewport, 'scroll')
  for (const query of [HOVER_QUERY, TOUCH_QUERY]) {
    const media = target?.matchMedia?.(query)
    if (media?.addEventListener) {
      media.addEventListener('change', update)
      cleanup.push(() => media.removeEventListener('change', update))
    } else if (media?.addListener) {
      media.addListener(update)
      cleanup.push(() => media.removeListener(update))
    }
  }
  update()
  return {
    stop() {
      if (stopped) return
      stopped = true
      cleanup.forEach(remove => remove())
    }
  }
}
